import { useEffect, useState, useRef, useCallback } from 'react'
import { api } from '../../lib/api'
import { Button } from '../ui/Button'

const PROVISIONING_STEPS = [
  'password_set',
  'streams_configured',
  'network_set',
  'osd_set',
  'vms_registered',
  'completed',
] as const

const STEP_LABELS: Record<string, string> = {
  password_set: 'Password',
  streams_configured: 'Streams',
  network_set: 'Network',
  osd_set: 'OSD',
  vms_registered: 'VMS Registration',
  completed: 'Security',
}

interface DeviceStatus {
  id: string
  site_device_id: string | null
  discovered_ip: string
  discovered_mac: string
  discovered_model: string
  status: string
  current_step: string | null
  error_message: string | null
  device_label: string | null
  hostname: string | null
  started_at: string | null
  completed_at: string | null
}

interface JobStatus {
  id: string
  status: string
  total_devices: number
  completed_devices: number
  failed_devices: number
  started_at: string | null
  completed_at: string | null
  profile_name: string
  devices: DeviceStatus[]
}

interface ProvisioningStatusProps {
  jobId: string
  onClose: () => void
}

export function ProvisioningStatus({ jobId, onClose }: ProvisioningStatusProps) {
  const [job, setJob] = useState<JobStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const loadStatus = useCallback(async () => {
    try {
      const res = await api.get<JobStatus>(`/provisioning-jobs/${jobId}/status`)
      setJob(res)
      // Stop polling if job is done
      if (res.status === 'completed' || res.status === 'failed') {
        if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null }
      }
    } catch { /* ignore */ }
    finally { setLoading(false) }
  }, [jobId])

  useEffect(() => {
    loadStatus()
    intervalRef.current = setInterval(loadStatus, 3000)
    return () => { if (intervalRef.current) clearInterval(intervalRef.current) }
  }, [loadStatus])

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>Loading...</div>
  if (!job) return <div style={{ padding: 40, color: 'var(--text-secondary)' }}>Job not found.</div>

  const isComplete = job.status === 'completed' || job.status === 'failed'
  const progress = job.total_devices > 0 ? Math.round(((job.completed_devices + job.failed_devices) / job.total_devices) * 100) : 0
  const elapsed = job.started_at ? formatElapsed(job.started_at, job.completed_at) : '--:--'

  function getStepIcon(device: DeviceStatus, step: string): string {
    const stepIndex = PROVISIONING_STEPS.indexOf(step as typeof PROVISIONING_STEPS[number])
    const currentIndex = device.current_step ? PROVISIONING_STEPS.indexOf(device.current_step as typeof PROVISIONING_STEPS[number]) : -1

    if (device.status === 'failed') return 'x'
    if (device.status === 'completed') return 'check'
    if (stepIndex < currentIndex) return 'check'
    if (stepIndex === currentIndex) return 'current'
    return 'pending'
  }

  function getDeviceIcon(status: string): string {
    switch (status) {
      case 'completed': return 'check'
      case 'failed': return 'x'
      case 'provisioning': case 'password_set': case 'streams_configured':
      case 'network_set': case 'osd_set': case 'vms_registered':
        return 'spinner'
      default: return 'queued'
    }
  }

  // Completion report
  if (isComplete) {
    const failed = job.devices.filter(d => d.status === 'failed')
    const succeeded = job.devices.filter(d => d.status === 'completed')

    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <Button onClick={onClose} variant="ghost" size="sm">
            &larr; Back
          </Button>
          <h2 style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)' }}>
            Provisioning {job.status === 'completed' ? 'Complete' : 'Failed'}
          </h2>
        </div>

        <div style={{
          padding: 24, maxWidth: 520,
          background: 'var(--bg-surface)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius)', boxShadow: 'var(--shadow-xs)',
        }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', marginBottom: 12 }}>
            {job.total_devices} cameras provisioned in {elapsed}
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
            <span style={{ color: 'var(--ok)' }}>{succeeded.length} successful</span>
            {failed.length > 0 && <> &middot; <span style={{ color: 'var(--err)' }}>{failed.length} failed</span></>}
          </div>

          {failed.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>Failed:</div>
              {failed.map(d => (
                <div key={d.id} style={{ fontSize: 12, color: 'var(--err)', marginBottom: 4, paddingLeft: 12 }}>
                  x <span style={!d.device_label && !d.hostname && d.discovered_ip ? { fontFamily: 'var(--font-mono)' } : {}}>{d.device_label || d.hostname || d.discovered_ip}</span> — {d.error_message || 'Unknown error'}
                </div>
              ))}
            </div>
          )}

          <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 16 }}>
            Site documentation has been updated:
            <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
              <li>IP addresses assigned</li>
              <li>MAC addresses recorded</li>
              <li>Serial numbers captured</li>
              <li>OSD text applied from labels</li>
            </ul>
          </div>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            {failed.length > 0 && (
              <Button variant="secondary" size="sm">
                Retry Failed
              </Button>
            )}
            <Button onClick={onClose} size="sm">
              Done
            </Button>
          </div>
        </div>
      </div>
    )
  }

  // In-progress dashboard
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Button onClick={onClose} variant="ghost" size="sm">
            &larr; Back
          </Button>
          <h2 style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)' }}>Provisioning In Progress</h2>
        </div>
        <span style={{ fontSize: 13, color: 'var(--text-tertiary)', fontVariantNumeric: 'tabular-nums' }}>{elapsed}</span>
      </div>

      {/* Progress bar */}
      <div style={{ marginBottom: 16 }}>
        <div style={{
          height: 8, borderRadius: 4, background: 'var(--border)',
          overflow: 'hidden', marginBottom: 6,
        }}>
          <div style={{
            height: '100%', borderRadius: 4,
            background: 'var(--primary)',
            width: `${progress}%`,
            transition: 'width 0.5s ease',
          }} />
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
          {job.completed_devices + job.failed_devices} / {job.total_devices} cameras ({progress}%)
        </div>
      </div>

      {/* Device list */}
      <div style={{
        background: 'var(--bg-surface)', border: '1px solid var(--border)',
        borderRadius: 'var(--radius)', overflow: 'hidden', maxHeight: 400, overflowY: 'auto',
      }}>
        {job.devices.map(device => {
          const icon = getDeviceIcon(device.status)
          return (
            <div key={device.id} style={{
              display: 'flex', alignItems: 'center', gap: 12,
              padding: '10px 16px',
              borderBottom: '1px solid var(--border-light, var(--border))',
            }}>
              <span style={{
                width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 12, flexShrink: 0,
                color: icon === 'check' ? 'var(--ok)' : icon === 'x' ? 'var(--err)' : icon === 'spinner' ? 'var(--accent)' : 'var(--text-tertiary)',
              }}>
                {icon === 'check' && <CheckIcon />}
                {icon === 'x' && <XIcon />}
                {icon === 'spinner' && <SpinnerIcon />}
                {icon === 'queued' && <ClockIcon />}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', ...(!device.device_label && !device.hostname && device.discovered_ip ? { fontFamily: 'var(--font-mono)' } : {}) }}>
                  {device.device_label || device.hostname || device.discovered_ip || 'Unknown'}
                </div>
                {device.current_step && device.status !== 'completed' && device.status !== 'failed' && (
                  <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
                    {STEP_LABELS[device.current_step] || device.current_step}...
                  </div>
                )}
                {device.error_message && (
                  <div style={{ fontSize: 11, color: 'var(--err)' }}>{device.error_message}</div>
                )}
              </div>
              {device.status === 'completed' && device.started_at && device.completed_at && (
                <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
                  [{formatElapsed(device.started_at, device.completed_at)}]
                </span>
              )}
            </div>
          )
        })}
      </div>

      {/* Current step details */}
      {job.devices.some(d => d.status !== 'completed' && d.status !== 'failed' && d.status !== 'discovered' && d.status !== 'matched') && (
        <div style={{
          padding: '12px 16px', marginTop: 12,
          background: 'var(--bg-surface)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius)', fontSize: 12, color: 'var(--text-secondary)',
        }}>
          {(() => {
            const active = job.devices.find(d =>
              d.status !== 'completed' && d.status !== 'failed' && d.status !== 'discovered' && d.status !== 'matched'
            )
            if (!active) return null
            return (
              <>
                <div style={{ marginBottom: 6 }}>
                  Current: <strong>{active.device_label || active.hostname || active.discovered_ip}</strong>
                  {active.current_step && ` — ${STEP_LABELS[active.current_step] || active.current_step}`}
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {PROVISIONING_STEPS.map(step => {
                    const icon = getStepIcon(active, step)
                    return (
                      <span key={step} style={{
                        fontSize: 11,
                        color: icon === 'check' ? 'var(--ok)' : icon === 'current' ? 'var(--primary)' : 'var(--text-tertiary)',
                      }}>
                        {icon === 'check' ? '\u2713' : icon === 'current' ? '\u25CF' : '\u25CB'} {STEP_LABELS[step]}
                      </span>
                    )
                  })}
                </div>
              </>
            )
          })()}
        </div>
      )}
    </div>
  )
}

function formatElapsed(start: string, end: string | null): string {
  const s = new Date(start).getTime()
  const e = end ? new Date(end).getTime() : Date.now()
  const totalSec = Math.floor((e - s) / 1000)
  const min = Math.floor(totalSec / 60)
  const sec = totalSec % 60
  return `${min}:${sec.toString().padStart(2, '0')}`
}

function CheckIcon() {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 6L9 17l-5-5" />
    </svg>
  )
}

function XIcon() {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 6L6 18M6 6l12 12" />
    </svg>
  )
}

function SpinnerIcon() {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
    </svg>
  )
}

function ClockIcon() {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <circle cx={12} cy={12} r={10} />
      <path d="M12 6v6l4 2" />
    </svg>
  )
}
